import { useEffect, useState, useCallback } from 'react';
import { Loader2, ShieldAlert, CheckCircle2, Ban, Search } from 'lucide-react';
import { fieldPartnersApi, type FraudFlag } from '@/api/field-partners';
import { formatDate } from '@/utils/formatters';
import { FieldProgramGate } from '@/components/field-partners/FieldProgramGate';
import type { FieldProgramConfig } from '@/hooks/useFieldProgramConfig';

const TYPE_LABELS: Record<FraudFlag['type'], string> = {
  MOCKED_GPS: 'Mocked GPS',
  DUPLICATE_PHOTO: 'Duplicate photo (pHash)',
  OUT_OF_RANGE: 'Out of range',
  OTP_FAILS: '5× OTP failures',
};

const TYPE_COLORS: Record<FraudFlag['type'], string> = {
  MOCKED_GPS: 'bg-red-50 text-red-700',
  DUPLICATE_PHOTO: 'bg-orange-50 text-orange-700',
  OUT_OF_RANGE: 'bg-amber-50 text-amber-700',
  OTP_FAILS: 'bg-yellow-50 text-yellow-700',
};

const STATUS_COLORS: Record<FraudFlag['status'], string> = {
  OPEN: 'bg-red-50 text-red-700',
  OK: 'bg-green-50 text-green-700',
  BLOCKED: 'bg-gray-800 text-white',
  INVESTIGATING: 'bg-blue-50 text-blue-700',
};

function FraudInner({ config }: { config: FieldProgramConfig | null }) {
  const partnerLabel = config?.partnerTypeLabel || 'Field Partner';

  const [rows, setRows] = useState<FraudFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fieldPartnersApi.listFraudAlerts();
      setRows(data);
    } catch (err: any) {
      if (err.response?.status === 404) setRows([]);
      else setError(err.response?.data?.error || 'Failed to load fraud flags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const resolve = async (id: string, action: 'MARK_OK' | 'BLOCK' | 'INVESTIGATE') => {
    try {
      await fieldPartnersApi.resolveFraud(id, action);
      await fetch();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Action failed');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Fraud Review</h1>
        <p className="text-sm text-[var(--text-muted)]">
          Flagged {partnerLabel.toLowerCase()} activity: GPS spoofing, photo duplication, out-of-range claims, OTP failures
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          </div>
        ) : error ? (
          <div className="py-20 text-center text-sm text-red-600">{error}</div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-[var(--text-muted)]">
            <ShieldAlert size={36} className="mb-2" />
            <p className="text-sm">No fraud flags open</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-inset)] text-left text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">{partnerLabel}</th>
                <th className="px-4 py-3">Detail</th>
                <th className="px-4 py-3">Detected</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-primary)]">
              {rows.map((f) => (
                <tr key={f.id} className="hover:bg-[var(--bg-surface-hover)]">
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_COLORS[f.type]}`}
                    >
                      {TYPE_LABELS[f.type]}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-[var(--text-primary)]">
                    {f.partnerName}
                  </td>
                  <td className="px-4 py-3 text-[var(--text-secondary)]">{f.detail}</td>
                  <td className="px-4 py-3 text-[var(--text-secondary)]">
                    {formatDate(f.detectedAt)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[f.status]}`}
                    >
                      {f.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {f.status === 'OPEN' && (
                        <>
                          <button
                            onClick={() => resolve(f.id, 'MARK_OK')}
                            title="Mark OK"
                            className="inline-flex items-center gap-1 rounded-md border border-green-300 bg-green-50 px-2 py-1 text-xs text-green-700 hover:bg-green-100"
                          >
                            <CheckCircle2 size={11} /> OK
                          </button>
                          <button
                            onClick={() => resolve(f.id, 'INVESTIGATE')}
                            title="Investigate"
                            className="inline-flex items-center gap-1 rounded-md border border-blue-300 bg-blue-50 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100"
                          >
                            <Search size={11} /> Investigate
                          </button>
                          <button
                            onClick={() => resolve(f.id, 'BLOCK')}
                            title={`Block ${partnerLabel.toLowerCase()}`}
                            className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100"
                          >
                            <Ban size={11} /> Block
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function FraudReviewPage() {
  return <FieldProgramGate>{(config) => <FraudInner config={config} />}</FieldProgramGate>;
}

export default FraudReviewPage;
